import React, { useState, useEffect } from 'react';
import { Copy, Check, FileText, GitCompare, RotateCcw, Eye, AlertCircle, X } from 'lucide-react';
import { humanFeedbackService } from '../../apis/humanFeedbackService';

const VersionDiffViewer = ({ 
  soapNoteId, 
  section, 
  currentVersion,
  onVersionRestored,
}) => {
  const [versionHistory, setVersionHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion1, setSelectedVersion1] = useState(null);
  const [selectedVersion2, setSelectedVersion2] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [viewMode, setViewMode] = useState('unified'); // 'unified' or 'side-by-side'
  const [copiedText, setCopiedText] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null);
  const [showAllSectionsModal, setShowAllSectionsModal] = useState(false);
  const [allSectionsData, setAllSectionsData] = useState(null);
  const [loadingAllSections, setLoadingAllSections] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (soapNoteId && section) {
      loadVersionHistory();
    }
  }, [soapNoteId, section]);

  useEffect(() => {
    if (selectedVersion1 && selectedVersion2 && selectedVersion1 !== selectedVersion2) {
      loadDiff();
    } else {
      setDiffData(null);
    }
  }, [selectedVersion1, selectedVersion2]);

  const loadVersionHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await humanFeedbackService.getSectionVersionHistory(soapNoteId, section);
      const versions = response.data.data.versions || [];
      
      // Sort by version descending (newest first)
      const sortedVersions = [...versions].sort((a, b) => b.version - a.version);
      setVersionHistory(sortedVersions);
      
      // Auto-select current version and previous version if available
      if (sortedVersions.length > 0) {
        setSelectedVersion2(sortedVersions[0].version); // Newest
        if (sortedVersions.length > 1) {
          setSelectedVersion1(sortedVersions[1].version); // Previous
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load version history');
      console.error('Error loading version history:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDiff = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await humanFeedbackService.compareSectionVersions(
        soapNoteId, 
        section, 
        selectedVersion1, 
        selectedVersion2
      );
      
      setDiffData(response.data.data);
    } catch (err) {
      setError(err.message || 'Failed to load version comparison');
      console.error('Error loading diff:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRestoreVersion = async (versionToRestore) => {
    try {
      setRestoring(true);
      setErrorMessage(null);
      
      const response = await humanFeedbackService.restoreSectionVersion(
        soapNoteId,
        section,
        versionToRestore,
        'version_restore'
      );

      setSuccessMessage(`Version ${versionToRestore} has been restored as version ${response.data.data.newVersion}`);
      setShowRestoreConfirm(null);

      if (onVersionRestored) {
        await onVersionRestored({
          section,
          restoredFromVersion: versionToRestore,
          newVersion: response.data.data.newVersion,
        });
      }
      
      // Reload version history to reflect the new version
      await loadVersionHistory();
      
      // Update selected versions
      if (selectedVersion2 === currentVersion) {
        setSelectedVersion2(response.data.data.newVersion);
      }
      if (selectedVersion1 === versionToRestore) {
        setSelectedVersion1(versionToRestore);
      }
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to restore version');
      console.error('Error restoring version:', err);
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setRestoring(false);
    }
  };

  const handleRetrieveAllSections = async (version) => {
    try {
      setLoadingAllSections(true);
      setErrorMessage(null);
      
      const response = await humanFeedbackService.getAllSectionsContentByVersion(soapNoteId, version);
      setAllSectionsData(response.data.data);
      setShowAllSectionsModal(true);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to retrieve all sections');
      console.error('Error retrieving all sections:', err);
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setLoadingAllSections(false);
    }
  };

  const renderDiffContent = () => {
    if (!diffData || !diffData.diff) return null;

    const { wordDiff, formattedDiff } = diffData.diff;
    
    if (viewMode === 'side-by-side') {
      return (
        <div className="grid grid-cols-2 gap-4">
          {/* Old Version */}
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3 pb-2 border-b">
              <h4 className="font-semibold text-gray-700">Version {diffData.oldVersion.version}</h4>
              <button
                onClick={() => copyToClipboard(diffData.oldVersion.content)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Copy version content"
              >
                {copiedText === diffData.oldVersion.content ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600" />
                )}
              </button>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
              {diffData.oldVersion.content || '(empty)'}
            </div>
          </div>

          {/* New Version */}
          <div className="border border-gray-300 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between mb-3 pb-2 border-b">
              <h4 className="font-semibold text-gray-700">Version {diffData.newVersion.version}</h4>
              <button
                onClick={() => copyToClipboard(diffData.newVersion.content)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Copy version content"
              >
                {copiedText === diffData.newVersion.content ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600" />
                )}
              </button>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
              {diffData.newVersion.content || '(empty)'}
            </div>
          </div>
        </div>
      );
    }

    // Unified view with track changes style
    return (
      <div className="border border-gray-300 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <div className="flex items-center gap-4">
            <h4 className="font-semibold text-gray-700">
              Comparing Version {diffData.oldVersion.version} → Version {diffData.newVersion.version}
            </h4>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 bg-red-100 text-red-800 rounded">Deleted</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">Added</span>
            </div>
          </div>
          <button
            onClick={() => copyToClipboard(diffData.newVersion.content)}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Copy current version content"
          >
            {copiedText === diffData.newVersion.content ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
        
        <div className="text-sm whitespace-pre-wrap leading-relaxed">
          {wordDiff.map((part, index) => {
            if (part.added) {
              return (
                <span
                  key={index}
                  className="bg-green-100 text-green-900 px-1 rounded"
                  title="Added text"
                >
                  {part.value}
                </span>
              );
            } else if (part.removed) {
              return (
                <span
                  key={index}
                  className="bg-red-100 text-red-900 line-through px-1 rounded"
                  title="Deleted text"
                >
                  {part.value}
                </span>
              );
            } else {
              return (
                <span key={index} className="text-gray-700">
                  {part.value}
                </span>
              );
            }
          })}
        </div>
      </div>
    );
  };

  const getVersionInfo = (version) => {
    return versionHistory.find(v => v.version === version);
  };

  if (loading && versionHistory.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (versionHistory.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No version history available for this section.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}
      
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Restore Version {showRestoreConfirm}?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will create a new version with the content from version {showRestoreConfirm}. 
              The current version will remain in history, and this restored version will become the new current version.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleRestoreVersion(showRestoreConfirm)}
                disabled={restoring}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {restoring ? 'Restoring...' : 'Yes, Restore'}
              </button>
              <button
                onClick={() => setShowRestoreConfirm(null)}
                disabled={restoring}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Sections Modal */}
      {showAllSectionsModal && allSectionsData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  All Sections - Version {allSectionsData.requestedVersion}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Complete SOAP note content from this version
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAllSectionsModal(false);
                  setAllSectionsData(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {['subjective', 'objective', 'assessment', 'plan'].map((sectionName) => {
                const sectionData = allSectionsData.sections[sectionName];
                return (
                  <div key={sectionName} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 capitalize">{sectionName}</h4>
                      {sectionData.version && (
                        <span className="text-xs text-gray-500">v{sectionData.version}</span>
                      )}
                    </div>
                    {sectionData.note && (
                      <p className="text-xs text-yellow-600 mb-2 italic">{sectionData.note}</p>
                    )}
                    <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                      {sectionData.content || '(empty)'}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => copyToClipboard(sectionData.content || '')}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center gap-1"
                      >
                        {copiedText === sectionData.content ? (
                          <>
                            <Check className="w-3 h-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowAllSectionsModal(false);
                  setAllSectionsData(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Version Selection */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compare From (Older Version)
            </label>
            <select
              value={selectedVersion1 || ''}
              onChange={(e) => setSelectedVersion1(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select version...</option>
              {versionHistory.map((v) => (
                <option key={v.version} value={v.version}>
                  Version {v.version} - {formatDate(v.lastEditedAt || v.createdAt)}
                  {v.version === currentVersion && ' (Current)'}
                </option>
              ))}
            </select>
            {selectedVersion1 && getVersionInfo(selectedVersion1) && (
              <div className="mt-2 text-xs text-gray-600">
                <p>Edited by: {getVersionInfo(selectedVersion1).lastEditedBy?.slice(-8) || 'Unknown'}</p>
                <p>Reason: {getVersionInfo(selectedVersion1).editReason || 'N/A'}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compare To (Newer Version)
            </label>
            <select
              value={selectedVersion2 || ''}
              onChange={(e) => setSelectedVersion2(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select version...</option>
              {versionHistory.map((v) => (
                <option key={v.version} value={v.version}>
                  Version {v.version} - {formatDate(v.lastEditedAt || v.createdAt)}
                  {v.version === currentVersion && ' (Current)'}
                </option>
              ))}
            </select>
            {selectedVersion2 && getVersionInfo(selectedVersion2) && (
              <div className="mt-2 text-xs text-gray-600">
                <p>Edited by: {getVersionInfo(selectedVersion2).lastEditedBy?.slice(-8) || 'Unknown'}</p>
                <p>Reason: {getVersionInfo(selectedVersion2).editReason || 'N/A'}</p>
              </div>
            )}
          </div>
        </div>

        {/* View Mode Toggle */}
        {diffData && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">View Mode:</span>
              <button
                onClick={() => setViewMode('unified')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  viewMode === 'unified'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Track Changes
              </button>
              <button
                onClick={() => setViewMode('side-by-side')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  viewMode === 'side-by-side'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Side by Side
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Statistics */}
      {diffData && diffData.statistics && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Change Statistics</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Words Added:</span>
              <span className="ml-2 font-semibold text-blue-900">{diffData.statistics.addedCount}</span>
            </div>
            <div>
              <span className="text-blue-700">Words Removed:</span>
              <span className="ml-2 font-semibold text-blue-900">{diffData.statistics.removedCount}</span>
            </div>
            <div>
              <span className="text-blue-700">Unchanged:</span>
              <span className="ml-2 font-semibold text-blue-900">{diffData.statistics.unchangedCount}</span>
            </div>
            <div>
              <span className="text-blue-700">Change %:</span>
              <span className="ml-2 font-semibold text-blue-900">{diffData.statistics.changePercentage}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Diff Content */}
      {loading && diffData ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : diffData ? (
        renderDiffContent()
      ) : (
        <div className="text-center py-8 text-gray-500">
          <GitCompare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Select two versions to compare</p>
        </div>
      )}

      {/* Version History List */}
      <div className="border border-gray-200 rounded-lg">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h4 className="font-medium text-gray-900">All Versions</h4>
          {versionHistory.length > 0 && (
            <button
              onClick={() => {
                const oldestVersion = versionHistory[versionHistory.length - 1].version;
                handleRetrieveAllSections(oldestVersion);
              }}
              disabled={loadingAllSections}
              className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              title="Retrieve all sections from oldest version"
            >
              <Eye className="w-3 h-3" />
              {loadingAllSections ? 'Loading...' : 'View All Sections'}
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-200">
          {versionHistory.map((version) => (
            <div
              key={version.version}
              className={`p-4 hover:bg-gray-50 transition-colors ${
                version.version === currentVersion ? 'bg-green-50' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <span className={`px-2 py-1 text-xs rounded-full border ${
                    version.version === currentVersion
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-gray-100 text-gray-800 border-gray-200'
                  }`}>
                    v{version.version}
                    {version.version === currentVersion && ' (Current)'}
                  </span>
                  <div className="text-sm text-gray-700 flex-1">
                    <span className="font-medium">{formatDate(version.lastEditedAt || version.createdAt)}</span>
                    {version.editReason && (
                      <span className="ml-2 text-gray-500">• {version.editReason}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRetrieveAllSections(version.version)}
                    disabled={loadingAllSections}
                    className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    title="View all sections from this version"
                  >
                    <Eye className="w-3 h-3" />
                    All Sections
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedVersion2(version.version);
                      if (!selectedVersion1) {
                        const prevVersion = versionHistory.find(v => v.version < version.version);
                        if (prevVersion) setSelectedVersion1(prevVersion.version);
                      }
                    }}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    View
                  </button>
                  {version.version !== currentVersion && (
                    <button
                      onClick={() => setShowRestoreConfirm(version.version)}
                      disabled={restoring}
                      className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      title="Restore this version"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Restore
                    </button>
                  )}
                  <button
                    onClick={() => copyToClipboard(version.content)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    title="Copy version content"
                  >
                    {copiedText === version.content ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VersionDiffViewer;

